import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099CInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-C instructions for Tax Year 2025"
          description="Clear instructions for Form 1098-C for 2025. Learn filing requirements, reporting rules, and step-by-step guidance to report charitable contributions accurately"
          keywords=""
          canonical="resources/information-returns/form-1099-c-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-C</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-c-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-C at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-C,{' '}
                        </strong>{' '}
                        is used by creditors and applicable financial entities
                        to report the cancellation or forgiveness of{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $600 or more
                        </strong>{' '}
                        of a debtor's debt to the IRS.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Debtor (recipient) copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>,
                        the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          February 28,
                        </strong>{' '}
                        if filing on paper, or by{' '}
                        <strong style={{ fontWeight: 600 }}> March 31,</strong>{' '}
                        if filing electronically.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          This article covers
                        </strong>{' '}
                        line-by-line instructions, including creditor and debtor
                        information, the identifiable event date in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 1</strong>,
                        amount of debt discharged in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong>,
                        interest included in the canceled debt in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong>, debt
                        description in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 4</strong>,
                        debtor liability in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 5</strong>,
                        identifiable event code in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 6</strong>, and
                        the fair market value (FMV) of property in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 7</strong>.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}> Penalties:</strong>{' '}
                        Late or incorrect filing penalties range from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per form
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-C?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  IRS Form 1099-C, also known as the &quot;Cancellation of
                  Debt&quot; form, is used to report any debt you have forgiven
                  for someone else.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  This form is required when a debt of $600 or more is canceled,
                  such as forgiven credit card debt, written-off medical bills,
                  or foreclosed mortgages. It notifies the IRS that a debt has
                  been forgiven and provides a copy to the taxpayer who had the
                  debt for their tax records.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> The forgiven debt typically counts as taxable
                  income for the taxpayer.
                </Alert>

                {/* <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the 1099-NEC accurately.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form 1099-C?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  You need to file Form 1099-C if you are an &quot;applicable
                  financial entity&quot; that forgives or cancels a debt of $600
                  or more owed by a taxpayer. This includes businesses such as:
                </Typography>

                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Banks</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Credit unions</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Collection agencies</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Healthcare providers (for medical bills)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Mortgage lenders (for foreclosures)
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In summary, if you are a business that forgives a significant
                  debt, you will likely need to file a 1099-C.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-C due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Creditors must be aware of two important deadlines related to
                  Form 1099-C:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filing deadline with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for creditors to submit Form 1099-C to the IRS
                  depends on the filing method:
                </Typography>

                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Electronic filing deadline: March 31, 2026</strong>
                    <br />
                    If you are e-filing Form 1099-C with the IRS, the deadline
                    is March 31st of the year following the tax year for which
                    the form is being issued
                  </li>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                      mb: 3,
                      mt: 0,
                      borderLeft: '3px solid rgb(102, 60, 0)',
                    }}
                  >
                    <b> Note:</b> If the deadline falls on a federal holiday or
                    weekend, the form is due on the next business day.
                  </Alert>
                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Avoid the stress of last-minute 1099-C deadlines.
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now and enjoy hassle-free IRS
                          compliance and peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099cinstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Paper filing deadline: February 28, 2026*</strong>
                    <br /> If you are filing Form 1099-C with the IRS using
                    traditional paper methods, the deadline is February 28th of
                    the year following the tax year for which the form is
                    issued.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Recipient copies deadline: January 31, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Creditors must share copies of Form 1099-C to recipients by
                  January 31st of the year following the relevant tax year. This
                  allows debtors to accurately file their tax returns on time.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Missing these deadlines or providing incorrect information on
                  Form 1099-C can lead to IRS penalties. To avoid potential
                  penalties, creditors should take steps to ensure that Form
                  1099-C is filed and distributed both accurately and on time.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-C penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss filing Form 1099-C within the deadline, the IRS
                  will impose a penalty. Here are the Form 1099-C penalty rates:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Stack
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      E-file your 1099-C forms ahead of the deadline with
                      TaxZerone.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 198,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099cinstructions`}
                    >
                      E-file Form 1099-C
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-C Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-C.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Creditor details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-1099-c-creditor-details.webp" />
                  <source srcSet="/assets/images/form-1099-c-creditor-details.png" />
                  <img
                    src="/assets/images/form-1099-c-creditor-details.png"
                    loading="lazy"
                    alt="Form 1099-c-creditor details"
                    width={525}
                    height={208}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your name, complete address, and TIN (SSN if you&apos;re
                  an individual; EIN if you&apos;re a business).
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Debtor details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-c-debtor-details.webp" />
                  <source srcSet="/assets/images/form-1099-c-debtor-details.png" />
                  <img
                    src="/assets/images/form-1099-c-debtor-details.png"
                    loading="lazy"
                    alt="Form 1099-c-debtor details"
                    width={525}
                    height={284}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                  Enter the debtor’s name, complete address, and TIN (SSN for an
                  individual; EIN for a business).
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Date of identifiable event
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-c-date-identifiable.webp" />
                  <source srcSet="/assets/images/form-1099-c-date-identifiable.png" />
                  <img
                    src="/assets/images/form-1099-c-date-identifiable.png"
                    loading="lazy"
                    alt="Form 1099-c-box-1-date of identifiable event"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date when the debt was officially canceled.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Exception:</b> If you voluntarily cancel a debt before an
                  identifiable event occurs and choose to report this
                  cancellation, enter the actual date you canceled the debt.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Amount of Debt Discharged
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-c-debt-discharged.webp" />
                  <source srcSet="/assets/images/form-1099-c-debt-discharged.png" />
                  <img
                    src="/assets/images/form-1099-c-debt-discharged.png"
                    loading="lazy"
                    alt="Form 1099-c-box-2- amount of debt discharged"
                    width={217}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total amount of debt canceled. This amount must not
                  exceed the original debt minus any payments received, through
                  any of the following:
                </Typography>

                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>Settlement agreements</li>
                  <li style={{ marginBottom: 12 }}>Foreclosure sales</li>
                  <li style={{ marginBottom: 12 }}>
                    Short sales that partially satisfied the debt
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Any other form of partial repayment
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Interest if Included in Box 2
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-c-interest.webp" />
                  <source srcSet="/assets/images/form-1099-c-interest.png" />
                  <img
                    src="/assets/images/form-1099-c-interest.png"
                    loading="lazy"
                    alt="Form 1099-c-box-3-interest if included in box 2"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you included interest as part of the canceled debt amount
                  in Box 2, enter that interest amount here in Box 3.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important notes:
                  </Typography>
                  <ul style={{ marginBottom: 6 }}>
                    <li style={{ marginBottom: 12 }}>
                      Including interest in Box 2 is optional.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      If you did include interest in Box 2, you must report it
                      in Box 3.
                    </li>{' '}
                    <li style={{ marginBottom: 12 }}>
                      If you did not include interest in Box 2, leave Box 3
                      blank.
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Debt description
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-c-debt-description.webp" />
                  <source srcSet="/assets/images/form-1099-c-debt-description.png" />
                  <img
                    src="/assets/images/form-1099-c-debt-description.png"
                    loading="lazy"
                    alt="Form 1099-c-box-4-debt description"
                    width={432}
                    height={131}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Provide a specific description of the debt&apos;s origin.
                  Examples include:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>Student loan</li>
                  <li style={{ marginBottom: 12 }}>Mortgage</li>{' '}
                  <li style={{ marginBottom: 12 }}>Credit card expenditure</li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Be as detailed as possible in your description.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: 600, fontStyle: 'italic' }}
                >
                  Special case:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you are filing a combined Form 1099-C and 1099-A, also
                  include a description of the property associated with the
                  debt.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 5: Debtor&apos;s Personal Liability for Debt
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-c-personal-liability.webp" />
                  <source srcSet="/assets/images/form-1099-c-personal-liability.png" />
                  <img
                    src="/assets/images/form-1099-c-personal-liability.png"
                    loading="lazy"
                    alt="Form 1099-c-box-5-debtor's personal liability for debt"
                    width={432}
                    height={105}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if the debtor was personally responsible for
                  repaying the debt. When to check:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Put an &quot;X&quot; in the box if either of these conditions
                  is true:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    The debtor was personally liable when the debt was
                    originally created.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The debt was modified, and the debtor was personally liable
                    at the time of the most recent modification.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Identifiable Event Code
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-c-identifiable-events.webp" />
                  <source srcSet="/assets/images/form-1099-c-identifiable-events.png" />
                  <img
                    src="/assets/images/form-1099-c-identifiable-events.png"
                    loading="lazy"
                    alt="Form 1099-C-box-6-identifiable event code"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the appropriate code to indicate the nature of the
                  identifiable event that led to the debt cancellation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report the debt as canceled on the earlier of:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    The date an identifiable event occurs, or
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The date of actual discharge (if you choose to file Form
                    1099-C for the year of cancellation)
                  </li>{' '}
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Identifiable Event Codes
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 100 }}>
                            Code
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 700 }}>
                            Description
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>A</TableCell>
                          <TableCell>
                            Discharge in bankruptcy under title 11 of the U.S.
                            Code (See &quot;Exceptions&quot; section for
                            discharges not requiring reporting)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>B</TableCell>
                          <TableCell>
                            Cancellation due to receivership, foreclosure, or
                            similar federal nonbankruptcy or state court
                            proceeding
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>C</TableCell>
                          <TableCell>
                            Cancellation due to statute of limitations
                            expiration (Only when debtor&apos;s defense is
                            upheld in final court judgment with expired appeal
                            period)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>D</TableCell>
                          <TableCell>
                            Foreclosure election by creditor that legally bars
                            further debt collection (Applies to mortgage lenders
                            barred by local law after a &quot;power of
                            sale&quot; exercise)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>E</TableCell>
                          <TableCell>
                            Cancellation under probate or similar proceeding
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>F</TableCell>
                          <TableCell>
                            Discharge by agreement for less than full
                            consideration (e.g., short sales)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>G</TableCell>
                          <TableCell>
                            Cancellation due to creditor&apos;s policy or
                            decision to discontinue collection (Can be written
                            policy or established business practice)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>H</TableCell>
                          <TableCell>
                            Other actual discharge occurring before any of the
                            above identifiable events
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Fair Market Value (FMV) of Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-c-fair-market-value.webp" />
                  <source srcSet="/assets/images/form-1099-c-fair-market-value.png" />
                  <img
                    src="/assets/images/form-1099-c-fair-market-value.png"
                    loading="lazy"
                    alt="Form 1099-b-box-7-fair market value of property"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  This box applies only when filing a combined Form 1099-C and
                  1099-A. Enter the Fair Market Value (FMV) of the property as
                  follows:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    For foreclosure, execution, or similar sale:
                  </li>
                  <ul>
                    <li style={{ marginBottom: 12 }}>
                      Enter the FMV of the property
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Generally, use the gross foreclosure bid price as the FMV
                    </li>
                  </ul>
                  <li style={{ marginBottom: 12 }}>
                    For abandonment or voluntary conveyance to the lender in
                    lieu of foreclosure:
                  </li>
                  <ul>
                    <li style={{ marginBottom: 12 }}>
                      Enter the appraised value of the property.
                    </li>
                  </ul>
                </ol>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 1099-C
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can file Form 1099-C, either online filing or by postal
                  mail.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic Filing (E-filing)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS recommends e-filing due to its efficiency, accuracy,
                  and quick processing notifications. With{' '}
                  <Link href="/" sx={{ color: '#0202ff' }}>
                    {' '}
                    TaxZerone
                  </Link>
                  , you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form 1099-C
                  </Link>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Here&apos;s how to e-file Form 1099-C:
                </Typography>

                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the required information:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Creditor&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Debtor&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>Debt details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Review the entered information:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Ensure all details are correct and complete.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Transmit the form to the IRS:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Send the form securely through the e-file service.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Share a copy with the debtor:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Provide a copy of the form to the debtor.
                    </li>
                  </ol>
                </ol>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While less efficient, paper filing is still an option. Follow
                  these steps to file Form 1099-C on paper:
                </Typography>

                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>Download Form 1099-C:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Visit the official IRS website (
                      <Link
                        href="https://www.irs.gov/"
                        target="_blank"
                        sx={{ color: '#2020ff' }}
                      >
                        www.irs.gov
                      </Link>
                      ) to download the form.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>Print the form.</li>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                      mb: 3,
                      mt: 0,
                      borderLeft: '3px solid rgb(102, 60, 0)',
                    }}
                  >
                    <b> Note:</b> The IRS requires special red ink forms for
                    submission.
                  </Alert>
                  <li style={{ marginBottom: 16 }}>Complete the form:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 12 }}>
                      Use black ink to fill out all required fields, including:
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Payer&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Recipient&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>Debt details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>Make a copy:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Keep a copy for your records.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>Mail the original form:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Send the original form to the IRS address specified in the
                      Form 1099 General Instructions. The address varies based
                      on your state. Refer to the &quot;Where to Send Form
                      1099-C - Mailing Address&quot; section to find the correct
                      address for your state.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Send a copy to the debtor:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Provide a copy to the debtor by January 31st of the year
                      following the reportable transaction.
                    </li>
                  </ol>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important considerations for paper filing:
                  </Typography>
                  <ul style={{ marginBottom: 0 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail forms early to ensure timely arrival before the
                      deadline.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Use certified mail for proof of timely filing
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      Ensure all copies are legible.
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Alternatively, you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-c/"
                    sx={{ color: '#0202ff' }}
                    id="1099-c-mailing-address"
                  >
                    file Form 1099-C electronically
                  </Link>{' '}
                  for faster processing and delivery, reducing the risk of
                  delays or lost forms.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-C - Mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-C varies depending on your business location. Below is a
                  table summarizing the mailing address for Form 1099-C:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-C to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-File Form 1099-C with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before you start e-filing Form 1099-C, ensure you have all the
                  necessary information to facilitate a smooth and efficient
                  filing experience. Here’s what you need to gather:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required information for filing Form 1099-C:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Creditor details</li>
                  <li style={{ marginBottom: 16 }}>Debtor details</li>
                  <li style={{ marginBottom: 16 }}>
                    Amount of debt discharged
                  </li>
                  <li style={{ marginBottom: 16 }}>Interest</li>
                  <li style={{ marginBottom: 16 }}>
                    Fair market value of property
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Steps to e-file Form 1099-C using TaxZerone:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 1: Complete Form 1099-C</b>
                  <br />
                  Open Form 1099-C and fill out the necessary fields with the
                  information you have gathered.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and transmit the return</b>
                  <br />
                  Carefully review all the entered information to ensure
                  accuracy. Once verified, transmit the completed Form 1099-C to
                  the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3: Provide the debtor copy</b>
                  <br />
                  After successfully transmitting the form to the IRS, share a
                  copy of the completed Form 1099-C to the debtor.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing Form 1099-C with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  <Link
                    href="/information-returns/efile-irs-form-1099-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-filing Form 1099-C
                  </Link>{' '}
                  with TaxZerone offers several advantages, including:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>IRS form validations:</b> Automated checks for errors
                      or missing information to ensure accuracy before
                      submission.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> Email debtor copies:</b> Share debtor copies of Form
                      1099-C via email and eliminate the need for manual
                      mailing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Secure portal access (ZeroneVault):</b> Debtors can
                      access their return copies anytime, anywhere through the
                      secure ZeroneVault portal.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Competitive pricing:</b> Industry-leading rates,
                      starting as low as $0.59 per form.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By e-filing with TaxZerone, you can simplify your Form 1099-C
                  filing, save time, and reduce errors.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Maximize the efficiency of your tax filing by{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              choosing TaxZerone for e-filing Form 1099-C.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099cinstructions`}
              >
                E-file Form 1099-C
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099CInstructions;
